(() => {
  const config = window.RoyalCheckout || { root: '/', moneyFormat: '${{amount}}' };
  const root = config.root || '/';
  const qs = (selector, context = document) => context.querySelector(selector);
  const qsa = (selector, context = document) => Array.from(context.querySelectorAll(selector));
  let lastFocus = null;

  function formatMoney(cents, format = config.moneyFormat) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    const value = Number(cents || 0);
    const placeholder = /\{\{\s*(\w+)\s*\}\}/;
    const formatWithDelimiters = (number, precision = 2, thousands = ',', decimal = '.') => {
      if (Number.isNaN(number) || number == null) return '0';
      const parts = (number / 100).toFixed(precision).split('.');
      parts[0] = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
      return parts[1] ? parts.join(decimal) : parts[0];
    };
    const match = String(format).match(placeholder);
    if (!match) return formatWithDelimiters(value);
    let output;
    switch (match[1]) {
      case 'amount_no_decimals': output = formatWithDelimiters(value, 0); break;
      case 'amount_with_comma_separator': output = formatWithDelimiters(value, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': output = formatWithDelimiters(value, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': output = formatWithDelimiters(value, 2, "'", '.'); break;
      default: output = formatWithDelimiters(value, 2); break;
    }
    return String(format).replace(placeholder, output);
  }

  function showToast(message) {
    const toast = qs('#RcToast');
    if (!toast) return;
    toast.textContent = message;
    toast.classList.add('is-visible');
    clearTimeout(toast._timer);
    toast._timer = setTimeout(() => toast.classList.remove('is-visible'), 2600);
  }

  function trapFocus(container) {
    const focusable = qsa('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])', container);
    if (!focusable.length) return () => {};
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const handler = (event) => {
      if (event.key !== 'Tab') return;
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    };
    container.addEventListener('keydown', handler);
    return () => container.removeEventListener('keydown', handler);
  }

  let releaseMenuTrap = () => {};
  function openMenu() {
    const menu = qs('[data-mobile-menu]');
    const overlay = qs('[data-menu-overlay]');
    if (!menu) return;
    lastFocus = document.activeElement;
    menu.setAttribute('aria-hidden', 'false');
    menu.classList.add('is-open');
    if (overlay) { overlay.hidden = false; requestAnimationFrame(() => overlay.classList.add('is-open')); }
    document.body.classList.add('is-locked', 'menu-open');
    qs('[data-menu-open]')?.setAttribute('aria-expanded', 'true');
    releaseMenuTrap = trapFocus(menu);
    qs('[data-menu-close]', menu)?.focus();
  }
  function closeMenu() {
    const menu = qs('[data-mobile-menu]');
    const overlay = qs('[data-menu-overlay]');
    if (!menu || menu.getAttribute('aria-hidden') === 'true') return;
    menu.setAttribute('aria-hidden', 'true');
    menu.classList.remove('is-open');
    overlay?.classList.remove('is-open');
    setTimeout(() => { if (overlay) overlay.hidden = true; }, 250);
    document.body.classList.remove('is-locked', 'menu-open');
    qs('[data-menu-open]')?.setAttribute('aria-expanded', 'false');
    releaseMenuTrap();
    lastFocus?.focus();
  }

  let releaseCartTrap = () => {};
  function openCart() {
    const shell = qs('[data-cart-drawer]');
    if (!shell) return;
    lastFocus = document.activeElement;
    shell.setAttribute('aria-hidden', 'false');
    shell.classList.add('is-open');
    document.body.classList.add('is-locked', 'cart-drawer-open');
    const dialog = qs('.cart-drawer', shell);
    releaseCartTrap = trapFocus(dialog || shell);
    qs('[data-cart-close]', shell)?.focus();
  }
  function closeCart() {
    const shell = qs('[data-cart-drawer]');
    if (!shell || shell.getAttribute('aria-hidden') === 'true') return;
    shell.setAttribute('aria-hidden', 'true');
    shell.classList.remove('is-open');
    document.body.classList.remove('is-locked', 'cart-drawer-open');
    releaseCartTrap();
    lastFocus?.focus();
  }

  function updateCartCount(count) {
    qsa('[data-cart-count]').forEach((element) => {
      element.textContent = count;
      element.hidden = Number(count) === 0;
    });
  }

  function replaceCartDrawer(html, openAfter = false) {
    if (!html) return;
    const documentFragment = new DOMParser().parseFromString(html, 'text/html');
    const incoming = documentFragment.querySelector('[data-cart-drawer]');
    const current = qs('[data-cart-drawer]');
    if (incoming && current) current.replaceWith(incoming);
    if (openAfter) openCart();
  }

  async function fetchCartDrawer(openAfter = false) {
    const sectionUrl = `${root}cart?section_id=cart-drawer`;
    const [sectionResponse, cartResponse] = await Promise.all([
      fetch(sectionUrl, { credentials: 'same-origin' }),
      fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } })
    ]);
    if (!sectionResponse.ok || !cartResponse.ok) throw new Error('Unable to refresh cart.');
    replaceCartDrawer(await sectionResponse.text(), openAfter);
    const cart = await cartResponse.json();
    updateCartCount(cart.item_count);
    return cart;
  }

  async function addToCart(form) {
    const formData = new FormData(form);
    formData.append('sections', 'cart-drawer');
    formData.append('sections_url', window.location.pathname);
    const response = await fetch(`${root}cart/add.js`, { method: 'POST', headers: { Accept: 'application/json' }, body: formData });
    const data = await response.json();
    if (!response.ok) throw new Error(data.description || data.message || 'Unable to add this item.');
    if (data.sections && data.sections['cart-drawer']) replaceCartDrawer(data.sections['cart-drawer'], true);
    const cart = await fetch(`${root}cart.js`).then((res) => res.json());
    updateCartCount(cart.item_count);
    if (!(data.sections && data.sections['cart-drawer'])) await fetchCartDrawer(true);
  }

  async function changeCart(key, quantity) {
    const response = await fetch(`${root}cart/change.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity, sections: ['cart-drawer'], sections_url: window.location.pathname })
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.description || data.message || 'Unable to update cart.');
    updateCartCount(data.item_count);
    if (data.sections && data.sections['cart-drawer']) replaceCartDrawer(data.sections['cart-drawer'], true);
    else await fetchCartDrawer(true);
  }

  async function updateCartNote(note) {
    const response = await fetch(`${root}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ note })
    });
    if (!response.ok) throw new Error('Unable to save order note.');
    showToast('Order note saved');
  }

  function initPredictiveSearch() {
    qsa('[data-predictive-search]').forEach((form) => {
      if (form.dataset.ready === 'true') return;
      form.dataset.ready = 'true';
      const input = qs('input[type="search"]', form);
      const results = qs('[data-predictive-results]', form);
      let timer;
      if (!input || !results) return;
      input.addEventListener('input', () => {
        clearTimeout(timer);
        const term = input.value.trim();
        if (term.length < 2) { results.hidden = true; results.innerHTML = ''; input.setAttribute('aria-expanded', 'false'); return; }
        timer = setTimeout(async () => {
          try {
            const url = `${root}search/suggest?q=${encodeURIComponent(term)}&resources[type]=product,collection,page,article&resources[limit]=6&section_id=predictive-search`;
            const response = await fetch(url);
            if (!response.ok) throw new Error();
            const html = await response.text();
            const parsed = new DOMParser().parseFromString(html, 'text/html');
            results.innerHTML = parsed.querySelector('.shopify-section')?.innerHTML || html;
            results.hidden = false;
            input.setAttribute('aria-expanded', 'true');
          } catch (_) {
            results.hidden = true;
            input.setAttribute('aria-expanded', 'false');
          }
        }, 220);
      });
      input.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') { results.hidden = true; input.setAttribute('aria-expanded', 'false'); }
      });
      document.addEventListener('click', (event) => {
        if (!form.contains(event.target)) { results.hidden = true; input.setAttribute('aria-expanded', 'false'); }
      });
    });
  }

  function initProduct(section) {
    if (!section || section.dataset.productReady === 'true') return;
    section.dataset.productReady = 'true';
    const variantsNode = qs('[data-product-variants]', section);
    if (!variantsNode) return;
    let variants = [];
    try { variants = JSON.parse(variantsNode.textContent); } catch (_) { return; }
    const optionGroups = qsa('[data-option-position]', section);
    const variantInput = qs('[data-variant-id-input]', section);
    const submit = qs('[data-product-submit]', section);
    const submitText = qs('[data-product-submit-text]', section);
    const price = qs('[data-product-price]', section);
    const comparePrice = qs('[data-compare-price]', section);
    const sku = qs('[data-product-sku]', section);
    const inventory = qs('[data-product-inventory]', section);
    const discountBadge = qs('[data-product-discount]', section);
    const availableLabel = section.dataset.availableLabel || 'Add to cart';
    const soldOutLabel = section.dataset.soldOutLabel || 'Sold out';
    const unavailableLabel = section.dataset.unavailableLabel || 'Unavailable';

    const mediaItems = qsa('[data-media-id]', section);
    const galleryTrack = qs('[data-gallery-track]', section);
    const moveGallery = (direction) => {
      if (!mediaItems.length) return;
      let activeIndex = mediaItems.findIndex((item) => item.classList.contains('is-active'));
      if (activeIndex < 0 && galleryTrack) {
        const trackLeft = galleryTrack.getBoundingClientRect().left;
        activeIndex = mediaItems.reduce((best, item, index) => {
          const distance = Math.abs(item.getBoundingClientRect().left - trackLeft);
          return distance < best.distance ? { index, distance } : best;
        }, { index: 0, distance: Number.POSITIVE_INFINITY }).index;
      }
      const nextIndex = (activeIndex + direction + mediaItems.length) % mediaItems.length;
      activateMedia(section, mediaItems[nextIndex].dataset.mediaId, true);
    };
    qs('[data-gallery-prev]', section)?.addEventListener('click', () => moveGallery(-1));
    qs('[data-gallery-next]', section)?.addEventListener('click', () => moveGallery(1));

    if (galleryTrack && 'IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        const visible = entries.filter((entry) => entry.isIntersecting).sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (!visible || window.innerWidth > 989) return;
        const id = visible.target.dataset.mediaId;
        qsa('[data-media-id]', section).forEach((media) => media.classList.toggle('is-active', media === visible.target));
        qsa('[data-media-target]', section).forEach((thumb) => thumb.classList.toggle('is-active', String(thumb.dataset.mediaTarget) === String(id)));
      }, { root: galleryTrack, threshold: [0.55, 0.75] });
      mediaItems.forEach((item) => observer.observe(item));
    }

    const selectVariant = () => {
      const values = optionGroups.map((group) => qs('input:checked', group)?.value || '');
      const variant = variants.find((item) => item.options.every((option, index) => option === values[index]));
      optionGroups.forEach((group) => {
        const selectedLabel = qs('[data-selected-option]', group);
        if (selectedLabel) selectedLabel.textContent = qs('input:checked', group)?.value || '';
      });
      if (!variant) {
        if (submit) submit.disabled = true;
        if (submitText) submitText.textContent = unavailableLabel;
        return;
      }
      if (variantInput) variantInput.value = variant.id;
      if (price) price.textContent = formatMoney(variant.price);
      if (comparePrice) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) { comparePrice.hidden = false; comparePrice.textContent = formatMoney(variant.compare_at_price); }
        else { comparePrice.hidden = true; comparePrice.textContent = ''; }
      }
      if (sku) {
        sku.textContent = variant.sku || '';
        sku.closest('.product-sku')?.classList.toggle('is-empty', !variant.sku);
      }
      if (submit) submit.disabled = !variant.available;
      if (submitText) submitText.textContent = variant.available ? availableLabel : soldOutLabel;
      if (inventory) {
        inventory.textContent = variant.available ? 'In stock and ready to ship' : 'Currently unavailable';
        inventory.classList.toggle('product-inventory--unavailable', !variant.available);
      }
      if (discountBadge) {
        const saving = variant.compare_at_price && variant.compare_at_price > variant.price ? Math.round(((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100) : 0;
        discountBadge.hidden = saving <= 0;
        discountBadge.textContent = saving > 0 ? `Save ${saving}%` : '';
      }
      const mediaId = variant.featured_media?.id || variant.featured_image?.id;
      if (mediaId) activateMedia(section, mediaId);
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    };
    optionGroups.forEach((group) => group.addEventListener('change', selectVariant));

    qsa('[data-media-target]', section).forEach((button) => button.addEventListener('click', () => activateMedia(section, button.dataset.mediaTarget, true)));
    const delivery = qs('[data-delivery-date]', section);
    if (delivery) {
      const days = Number(qs('[data-delivery-days]', section)?.dataset.deliveryDays || section.querySelector('.delivery-estimate')?.dataset.deliveryDays || 7);
      const date = new Date();
      date.setDate(date.getDate() + days);
      delivery.textContent = new Intl.DateTimeFormat(document.documentElement.lang || 'en', { month: 'short', day: 'numeric' }).format(date);
    }
  }

  function activateMedia(section, id, scroll = false) {
    qsa('[data-media-id]', section).forEach((media) => {
      const active = String(media.dataset.mediaId) === String(id);
      media.classList.toggle('is-active', active);
      media.setAttribute('aria-hidden', String(!active));
      if (!active) media.querySelectorAll('video').forEach((video) => video.pause());
    });
    qsa('[data-media-target]', section).forEach((thumb) => {
      const active = String(thumb.dataset.mediaTarget) === String(id);
      thumb.classList.toggle('is-active', active);
      if (active) thumb.setAttribute('aria-current', 'true'); else thumb.removeAttribute('aria-current');
    });
    const target = qs(`[data-media-id="${id}"]`, section);
    if (scroll && target) target.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
  }

  function initRecommendations() {
    qsa('[data-recommendations-url]').forEach(async (section) => {
      if (section.dataset.loaded === 'true') return;
      section.dataset.loaded = 'true';
      try {
        const response = await fetch(section.dataset.recommendationsUrl);
        if (!response.ok) return;
        const html = await response.text();
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const content = parsed.querySelector('[data-recommendations-url]');
        if (content && content.innerHTML.trim()) section.innerHTML = content.innerHTML;
      } catch (_) {}
    });
  }

  document.addEventListener('click', async (event) => {
    if (event.target.closest('[data-menu-open]')) openMenu();
    if (event.target.closest('[data-menu-close], [data-menu-overlay]')) closeMenu();
    if (event.target.closest('[data-cart-open]')) openCart();
    if (event.target.closest('[data-cart-close]')) closeCart();

    const qtyButton = event.target.closest('[data-qty-minus], [data-qty-plus]');
    if (qtyButton) {
      const input = qs('input[type="number"]', qtyButton.closest('.quantity'));
      if (input) {
        const delta = qtyButton.hasAttribute('data-qty-plus') ? 1 : -1;
        input.value = Math.max(Number(input.min || 1), Number(input.value || 1) + delta);
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    const change = event.target.closest('[data-cart-change]');
    if (change) {
      change.disabled = true;
      try { await changeCart(change.dataset.cartChange, Number(change.dataset.quantity)); }
      catch (error) { showToast(error.message); change.disabled = false; }
    }
  });

  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('form[data-ajax-cart]');
    if (!form) return;
    if (event.submitter && event.submitter.classList.contains('shopify-payment-button__button')) return;
    event.preventDefault();
    const button = qs('[type="submit"]', form);
    const wasDisabled = button ? button.disabled : false;
    button?.setAttribute('aria-busy', 'true');
    if (button) button.disabled = true;
    try { await addToCart(form); showToast('Added to cart'); }
    catch (error) { showToast(error.message); }
    finally { button?.removeAttribute('aria-busy'); if (button) button.disabled = wasDisabled; }
  });

  let noteTimer;
  document.addEventListener('input', (event) => {
    if (!event.target.matches('[data-cart-note]')) return;
    clearTimeout(noteTimer);
    noteTimer = setTimeout(() => updateCartNote(event.target.value).catch((error) => showToast(error.message)), 500);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') { closeMenu(); closeCart(); }
  });

  initPredictiveSearch();
  qsa('[data-product-section]').forEach(initProduct);
  initRecommendations();
  document.addEventListener('shopify:section:load', (event) => {
    initPredictiveSearch();
    qsa('[data-product-section]', event.target).forEach(initProduct);
    initRecommendations();
  });
})();


(() => {
  const config = window.RoyalCheckout || { root: '/', moneyFormat: '${{amount}}' };
  const key = 'royal-checkout-wishlist-v2';
  const invalidLegacyLabel = ['discover', 'ed'].join('');
  const legacyKeys = ['royal-checkout-wishlist-v1', 'wishlist', invalidLegacyLabel];
  const cleanHandle = (value) => typeof value === 'string' && /^[a-z0-9][a-z0-9-]*$/i.test(value) && value.toLowerCase() !== invalidLegacyLabel ? value : null;
  const parseItems = (raw) => {
    try {
      const value = JSON.parse(raw || '[]');
      const list = Array.isArray(value) ? value : [];
      return Array.from(new Set(list.map((item) => cleanHandle(typeof item === 'string' ? item : item?.handle)).filter(Boolean)));
    } catch (_) { return []; }
  };
  const migrate = () => {
    let items = parseItems(localStorage.getItem(key));
    legacyKeys.forEach((legacyKey) => {
      if (legacyKey === key) return;
      items = Array.from(new Set([...items, ...parseItems(localStorage.getItem(legacyKey))]));
      localStorage.removeItem(legacyKey);
    });
    localStorage.setItem(key, JSON.stringify(items));
    return items;
  };
  const getItems = () => parseItems(localStorage.getItem(key));
  const setItems = (items) => { localStorage.setItem(key, JSON.stringify(Array.from(new Set(items.map(cleanHandle).filter(Boolean))))); sync(); };
  const notify = (message) => {
    const toast = document.getElementById('RcToast');
    if (!toast) return;
    toast.textContent = message;
    toast.classList.add('is-visible');
    window.clearTimeout(notify.timer);
    notify.timer = window.setTimeout(() => toast.classList.remove('is-visible'), 2200);
  };
  const money = (cents) => {
    const amount = (Number(cents || 0) / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return String(config.moneyFormat || '${{amount}}').replace(/\{\{\s*amount\s*\}\}/, amount).replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.round(Number(cents || 0) / 100).toLocaleString());
  };
  function sync() {
    const items = getItems();
    document.querySelectorAll('[data-wishlist-count]').forEach((badge) => { badge.textContent = items.length; badge.hidden = items.length === 0; });
    document.querySelectorAll('[data-wishlist-toggle]').forEach((button) => {
      const handle = cleanHandle(button.dataset.wishlistToggle);
      const active = handle ? items.includes(handle) : false;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', String(active));
      button.setAttribute('aria-label', active ? 'Remove product from wishlist' : 'Add product to wishlist');
    });
  }
  async function renderWishlist() {
    const grid = document.querySelector('[data-wishlist-grid]');
    if (!grid) return;
    const empty = document.querySelector('[data-wishlist-empty]');
    const items = getItems();
    if (!items.length) { grid.innerHTML = ''; if (empty) empty.hidden = false; return; }
    if (empty) empty.hidden = true;
    const products = (await Promise.all(items.map(async (handle) => {
      try { const response = await fetch(`${config.root}products/${encodeURIComponent(handle)}.js`); return response.ok ? await response.json() : null; }
      catch (_) { return null; }
    }))).filter(Boolean);
    const validHandles = products.map((product) => cleanHandle(product.handle)).filter(Boolean);
    if (validHandles.length !== items.length) setItems(validHandles);
    if (!products.length) { grid.innerHTML = ''; if (empty) empty.hidden = false; return; }
    grid.innerHTML = products.map((product) => {
      const image = product.featured_image || product.images?.[0] || '';
      const title = String(product.title || 'Product').replace(/[&<>"]/g, (character) => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;'}[character]));
      const url = `${config.root}products/${encodeURIComponent(product.handle)}`;
      return `<article class="product-card wishlist-product-card"><button class="wishlist-toggle wishlist-remove is-active" type="button" data-wishlist-toggle="${product.handle}" aria-pressed="true" aria-label="Remove product from wishlist"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.7l-1.1-1.1a5.5 5.5 0 0 0-7.8 7.8L12 21l8.8-8.6a5.5 5.5 0 0 0 0-7.8Z"/></svg></button><a class="product-card__media" href="${url}">${image ? `<img src="${image}" alt="${title}" loading="lazy">` : ''}</a><div class="product-card__body"><h3><a href="${url}">${title}</a></h3><div class="price"><span>${money(product.price)}</span></div><a class="button button--small" href="${url}">View product</a></div></article>`;
    }).join('');
    sync();
  }
  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-wishlist-toggle]');
    if (!button) return;
    event.preventDefault();
    const handle = cleanHandle(button.dataset.wishlistToggle);
    if (!handle) return;
    const items = getItems();
    const removing = items.includes(handle);
    setItems(removing ? items.filter((item) => item !== handle) : [...items, handle]);
    notify(removing ? 'Removed from wishlist' : 'Added to wishlist');
    if (document.querySelector('[data-wishlist-grid]')) renderWishlist();
  });
  migrate();
  sync();
  renderWishlist();
  document.addEventListener('shopify:section:load', () => { sync(); renderWishlist(); });

  const enforceFooterFlow = () => {
    const shell = document.querySelector('[data-page-footer-shell]');
    if (!shell) return;

    const utilitySelectors = [
      '#shopify-section-cart-drawer',
      '[id$="__mobile_nav"]',
      '[id$="__chat"]'
    ];

    shell.querySelectorAll(utilitySelectors.join(',')).forEach((wrapper) => {
      wrapper.style.setProperty('width', '0', 'important');
      wrapper.style.setProperty('height', '0', 'important');
      wrapper.style.setProperty('min-width', '0', 'important');
      wrapper.style.setProperty('min-height', '0', 'important');
      wrapper.style.setProperty('max-width', '0', 'important');
      wrapper.style.setProperty('max-height', '0', 'important');
      wrapper.style.setProperty('margin', '0', 'important');
      wrapper.style.setProperty('padding', '0', 'important');
      wrapper.style.setProperty('border', '0', 'important');
      wrapper.style.setProperty('overflow', 'visible', 'important');
    });

    const footerSection = shell.querySelector('[id$="__footer"]');
    if (footerSection && footerSection !== shell.lastElementChild) {
      shell.appendChild(footerSection);
    }
  };

  enforceFooterFlow();
  document.addEventListener('DOMContentLoaded', enforceFooterFlow);
  document.addEventListener('shopify:section:load', enforceFooterFlow);
  document.addEventListener('shopify:section:reorder', enforceFooterFlow);


  const closeDesktopMenus = (except = null) => {
    document.querySelectorAll('[data-desktop-menu][open]').forEach((details) => {
      if (details !== except) details.removeAttribute('open');
    });
  };

  document.addEventListener('toggle', (event) => {
    const details = event.target.closest && event.target.closest('[data-desktop-menu]');
    if (details && details.open) closeDesktopMenus(details);
  }, true);

  document.addEventListener('click', (event) => {
    if (!event.target.closest('[data-desktop-menu]')) closeDesktopMenus();
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closeDesktopMenus();
  });

})();
/* ===== Cart page quantity stepper ===== */
;(() => { const rcBuildQtySteppers = () => { const form = document.querySelector('form.cart-page'); if (!form) return; form.querySelectorAll('input[name="updates[]"]').forEach((input) => { if (input.closest('.qty-stepper')) return; const label = input.closest('label'); const field = document.createElement('div'); field.className = 'cart-qty-field'; const caption = document.createElement('span'); caption.className = 'cart-qty-field__label'; caption.textContent = 'Quantity'; const box = document.createElement('div'); box.className = 'quantity qty-stepper'; const minus = document.createElement('button'); minus.type = 'button'; minus.className = 'qty-stepper__btn'; minus.setAttribute('data-qty-minus', ''); minus.setAttribute('aria-label', 'Decrease quantity'); minus.innerHTML = '&minus;'; const plus = document.createElement('button'); plus.type = 'button'; plus.className = 'qty-stepper__btn'; plus.setAttribute('data-qty-plus', ''); plus.setAttribute('aria-label', 'Increase quantity'); plus.innerHTML = '+'; const anchor = label || input; anchor.parentNode.insertBefore(field, anchor); field.appendChild(caption); field.appendChild(box); box.appendChild(minus); box.appendChild(input); box.appendChild(plus); input.setAttribute('min', '1'); input.setAttribute('aria-label', 'Quantity'); if (label && label.parentNode) label.parentNode.removeChild(label); }); if (form.dataset.qtyReady === 'true') return; form.dataset.qtyReady = 'true'; let timer = null; form.addEventListener('change', (event) => { if (!event.target.matches('input[name="updates[]"]')) return; const updateButton = form.querySelector('[name="update"]'); if (!updateButton) return; window.clearTimeout(timer); form.classList.add('cart-page--updating'); timer = window.setTimeout(() => { updateButton.click(); }, 500); }); }; if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', rcBuildQtySteppers); else rcBuildQtySteppers(); })();